/**
 * StockEntryItemsWidget — Product search + item card list for Stock Entry.
 *
 * Replaces the raw child table with a Shopee/Lazada-style product picker:
 * - Search bar to find items by name or SKU
 * - Each added item shows as a card with qty stepper + delete
 * - Maps to ERPNext's `items` child table (Stock Entry Detail)
 *
 * Hidden until stock_entry_type is selected (progressive disclosure).
 * Follows TemplateInfoWidget pattern (Plan 126).
 */

import { useState, useCallback } from "react";
import { Plus, Minus, Trash2, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DocTypeSearchAutocomplete } from "@/components/search/DocTypeSearchAutocomplete";
import type { ExperienceWidgetProps } from "@/lib/views/widgets/registry";
import { SectionHeader } from "@/features/inventory/item-detail/components/SectionHeader";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { getERPClient } from "@/lib/erp/client";

const i18n: Record<string, { en: string; th: string }> = {
  search_placeholder: { en: "Search item by name or SKU...", th: "ค้นหาสินค้าด้วยชื่อหรือ SKU..." },
  add_item: { en: "Add Item", th: "เพิ่มสินค้า" },
  no_items: { en: "No items added yet", th: "ยังไม่มีสินค้า" },
  no_items_hint: { en: "Search and add items above", th: "ค้นหาและเพิ่มสินค้าด้านบน" },
  qty: { en: "Qty", th: "จำนวน" },
  items_count: { en: "items", th: "รายการ" },
};

interface StockEntryItem {
  item_code: string;
  item_name?: string;
  qty: number;
  uom?: string;
  stock_uom?: string;
  conversion_factor?: number;
  idx: number;
  /** Track client-side rows before save */
  __isLocal?: boolean;
}

export function StockEntryItemsWidget({
  formData,
  onFieldChange,
  disabled,
  isSaving,
}: ExperienceWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const stockEntryType = (formData.stock_entry_type as string) || "";
  const [searchValue, setSearchValue] = useState("");

  // Items from formData (Stock Entry Detail child table)
  const items: StockEntryItem[] = Array.isArray(formData.items) ? formData.items : [];

  const updateItems = useCallback(
    (newItems: StockEntryItem[]) => {
      // Re-index
      const indexed = newItems.map((item, i) => ({ ...item, idx: i + 1 }));
      onFieldChange("items", indexed);
    },
    [onFieldChange]
  );

  const handleAddItem = useCallback(
    async (itemCode: string) => {
      if (!itemCode) return;

      // Check if already added
      const existing = items.find((i) => i.item_code === itemCode);
      if (existing) {
        // Increment qty
        updateItems(
          items.map((i) =>
            i.item_code === itemCode ? { ...i, qty: i.qty + 1 } : i
          )
        );
        return;
      }

      // Fetch stock_uom + item_name from Item doc — Stock Entry Detail child
      // table requires `uom` and `conversion_factor` on every row, otherwise
      // the backend rejects the save with "UOM is required, Conversion
      // Factor is required". In ERPNext Desk the Item link handler fills
      // these automatically; the custom widget has to do it explicitly.
      let stockUom = "";
      let itemName = "";
      try {
        const client = getERPClient();
        const data = await client.call<{
          stock_uom: string;
          item_name: string;
        }>("frappe.client.get_value", {
          doctype: "Item",
          fieldname: ["stock_uom", "item_name"],
          filters: { name: itemCode },
        });
        stockUom = data?.stock_uom || "";
        itemName = data?.item_name || "";
      } catch {
        // If fetch fails, row is added anyway; backend will surface the
        // UOM error and the user can retry after re-picking.
      }

      updateItems([
        ...items,
        {
          item_code: itemCode,
          item_name: itemName,
          qty: 1,
          uom: stockUom,
          stock_uom: stockUom,
          conversion_factor: 1,
          idx: items.length + 1,
          __isLocal: true,
        },
      ]);
    },
    [items, updateItems]
  );

  const handleQtyChange = useCallback(
    (itemCode: string, newQty: number) => {
      if (newQty < 1) return;
      updateItems(
        items.map((i) =>
          i.item_code === itemCode ? { ...i, qty: newQty } : i
        )
      );
    },
    [items, updateItems]
  );

  const handleRemove = useCallback(
    (itemCode: string) => {
      updateItems(items.filter((i) => i.item_code !== itemCode));
    },
    [items, updateItems]
  );

  // Don't render until type is selected
  if (!stockEntryType) {
    return null;
  }

  return (
    <div>
      <SectionHeader
        label={getSectionTitleTranslation("Items", language)}
        subtitle={
          items.length > 0
            ? `${items.length} ${t("items_count")}`
            : undefined
        }
      />
      <div className="rounded-2xl overflow-hidden bg-card shadow-apple p-5 space-y-4">
        {/* Search + Add — only add items on explicit dropdown selection */}
        <DocTypeSearchAutocomplete
          doctype="Item"
          value={searchValue}
          onChange={(val) => setSearchValue(val)}
          onSelect={(val) => {
            handleAddItem(val);
            setSearchValue("");
          }}
          placeholder={t("search_placeholder")}
          disabled={disabled || isSaving}
          showInitialSuggestions
          minSearchLength={0}
          filters={{ has_variants: 0 }}
        />

        {/* Item cards */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-muted mb-3">
              <Package className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium text-muted-foreground">
              {t("no_items")}
            </div>
            <div className="text-xs text-muted-foreground/60 mt-1">
              {t("no_items_hint")}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.item_code}
                className="flex items-center gap-4 rounded-xl border border-border/40 bg-background p-4"
              >
                {/* Item info */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground truncate">
                    {item.item_code}
                  </div>
                  {item.item_name && item.item_name !== item.item_code && (
                    <div className="text-xs text-muted-foreground truncate mt-0.5">
                      {item.item_name}
                    </div>
                  )}
                </div>

                {/* Qty stepper */}
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-lg"
                    onClick={() => handleQtyChange(item.item_code, item.qty - 1)}
                    disabled={disabled || isSaving || item.qty <= 1}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <Input
                    type="number"
                    min={1}
                    value={item.qty}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1) {
                        handleQtyChange(item.item_code, val);
                      }
                    }}
                    className="h-8 w-16 text-center text-sm font-semibold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={disabled || isSaving}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 rounded-lg"
                    onClick={() => handleQtyChange(item.item_code, item.qty + 1)}
                    disabled={disabled || isSaving}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>

                {/* Delete */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0"
                  onClick={() => handleRemove(item.item_code)}
                  disabled={disabled || isSaving}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
